import {
  Box,
  Drawer,
  IconButton,
  Paper,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import MenuRounded from "@mui/icons-material/MenuRounded";
import ChevronLeftRounded from "@mui/icons-material/ChevronLeftRounded";
import ChevronRightRounded from "@mui/icons-material/ChevronRightRounded";
import { Outlet, useNavigate } from "react-router-dom";
import { Link as RouterLink } from "react-router-dom";
import { useState, useEffect } from "react";
import { CompBreadcrumb } from "../components/navigation/Breadcrumb";
import { SidebarNav } from "../components/navigation/SidebarNav";
import { CommandPalette } from "../components/command-palette/CommandPalette";
import { useAuth } from "../contexts/AuthContext";
import { Panel } from "../components/layout/Panel";

const SIDEBAR_EXPANDED_KEY = "om-sidebar-expanded";
const SIDEBAR_WIDTH_EXPANDED = 280;
const SIDEBAR_WIDTH_COLLAPSED = 64;

function getInitialSidebarExpanded(): boolean {
  try {
    const stored = localStorage.getItem(SIDEBAR_EXPANDED_KEY);
    return stored !== "false";
  } catch {
    return true;
  }
}

export function ShellLayout() {
  const auth = useAuth();
  const navigate = useNavigate();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));

  const [sidebarExpanded, setSidebarExpanded] = useState(getInitialSidebarExpanded);
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
  const [commandPaletteOpen, setCommandPaletteOpen] = useState(false);

  useEffect(() => {
    try {
      localStorage.setItem(SIDEBAR_EXPANDED_KEY, String(sidebarExpanded));
    } catch {
      // ignore
    }
  }, [sidebarExpanded]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key !== "k" || (!e.metaKey && !e.ctrlKey)) return;
      const target = e.target as HTMLElement;
      const isInput =
        target.tagName === "INPUT" ||
        target.tagName === "TEXTAREA" ||
        target.isContentEditable;
      if (isInput) return;
      e.preventDefault();
      setCommandPaletteOpen((open) => !open);
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, []);

  const handleLogout = () => {
    auth.logout();
    navigate("/login", { replace: true });
  };

  const sidebarWidth = sidebarExpanded ? SIDEBAR_WIDTH_EXPANDED : SIDEBAR_WIDTH_COLLAPSED;

  const sidebarContent = (
    <Box
      sx={{
        width: sidebarWidth,
        height: "100%",
        display: "flex",
        flexDirection: "column",
        bgcolor: "background.paper",
        borderRight: "1px solid",
        borderColor: "divider",
      }}
    >
      <Box
        sx={{
          py: 2,
          px: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: sidebarExpanded ? "space-between" : "center",
          borderBottom: "1px solid",
          borderColor: "divider",
        }}
      >
        {sidebarExpanded && (
          <Typography
            component={RouterLink}
            to="/home"
            variant="h6"
            noWrap
            sx={{
              fontWeight: 600,
              textDecoration: "none",
              color: "inherit",
              px: 1,
            }}
          >
            OpenMon
          </Typography>
        )}
        <IconButton
          size="small"
          onClick={() => setSidebarExpanded((v) => !v)}
          aria-label={sidebarExpanded ? "Collapse sidebar" : "Expand sidebar"}
          sx={{ flexShrink: 0 }}
        >
          {sidebarExpanded ? (
            <ChevronLeftRounded fontSize="small" />
          ) : (
            <ChevronRightRounded fontSize="small" />
          )}
        </IconButton>
      </Box>
      <SidebarNav
        collapsed={!sidebarExpanded}
        onOpenCommandPalette={() => setCommandPaletteOpen(true)}
        onLogout={handleLogout}
      />
    </Box>
  );

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        minHeight: "100vh",
        bgcolor: "background.default",
      }}
    >
      {/* <AppBar
        position="static"
        elevation={0}
        sx={{
          borderBottom: "1px solid",
          borderColor: "divider",
          background: "linear-gradient(to right, rgba(15,23,42,0.98), rgba(15,23,42,0.92))",
        }}
      >
        <Toolbar sx={{ gap: 1.5, flexWrap: "wrap" }}>
          {!isDesktop && (
            <IconButton
              color="inherit"
              edge="start"
              onClick={() => setMobileDrawerOpen(true)}
              aria-label="Open menu"
            >
              <MenuRounded />
            </IconButton>
          )}
          <Typography
            component={RouterLink}
            to="/home"
            variant="h6"
            noWrap
            sx={{
              fontWeight: 600,
              textDecoration: "none",
              color: "inherit",
            }}
          >
            OpenMon
          </Typography>
          <CompBreadcrumb />
          <Box sx={{ flexGrow: 1 }} />
          <Tooltip title="Search (Ctrl+K)" enterDelay={300}>
            <IconButton
              color="inherit"
              size="small"
              aria-label="Open command palette (Ctrl+K)"
              onClick={() => setCommandPaletteOpen(true)}
              sx={{ opacity: 0.9 }}
            >
              <SearchRounded fontSize="small" />
            </IconButton>
          </Tooltip>
          <Button
            color="inherit"
            size="small"
            startIcon={<LogoutRounded />}
            onClick={handleLogout}
            sx={{ opacity: 0.9 }}
          >
            Log out
          </Button>
        </Toolbar>
      </AppBar> */}

      <Paper
        elevation={0}
        sx={{
          display: "flex",
          flex: 1,
          border: "none"
        }}
      >
        {isDesktop ? (
          <Drawer
            variant="permanent"
            sx={{
              width: sidebarWidth,
              flexShrink: 0,
              "& .MuiDrawer-paper": {
                width: sidebarWidth,
                boxSizing: "border-box",
                top: "auto",
                borderTop: "none",
                borderLeft: "none",
                borderBottom: "none",
                transition: theme.transitions.create("width", {
                  easing: theme.transitions.easing.sharp,
                  duration: theme.transitions.duration.enteringScreen,
                }),
                overflowX: "hidden",
              },
            }}
          >
            {sidebarContent}
          </Drawer>
        ) : (
          <Drawer
            variant="temporary"
            open={mobileDrawerOpen}
            onClose={() => setMobileDrawerOpen(false)}
            ModalProps={{ keepMounted: true }}
            sx={{
              "& .MuiDrawer-paper": {
                width: SIDEBAR_WIDTH_EXPANDED,
                boxSizing: "border-box",
              },
            }}
          >
            <Box
              sx={{
                width: SIDEBAR_WIDTH_EXPANDED,
                height: "100%",
                display: "flex",
                flexDirection: "column",
                bgcolor: "background.paper",
                borderRight: "1px solid",
                borderColor: "divider",
              }}
            >
              <Box
                sx={{
                  py: 2,
                  px: 2,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  borderBottom: "1px solid",
                  borderColor: "divider",
                }}
              >
                <Typography
                  component={RouterLink}
                  to="/home"
                  variant="h6"
                  noWrap
                  onClick={() => setMobileDrawerOpen(false)}
                  sx={{
                    fontWeight: 600,
                    textDecoration: "none",
                    color: "inherit",
                  }}
                >
                  OpenMon
                </Typography>
                <IconButton
                  size="small"
                  onClick={() => setMobileDrawerOpen(false)}
                  aria-label="Close menu"
                >
                  <ChevronLeftRounded fontSize="small" />
                </IconButton>
              </Box>
              <SidebarNav
                collapsed={false}
                onNavigate={() => setMobileDrawerOpen(false)}
                onOpenCommandPalette={() => {
                  setCommandPaletteOpen(true);
                  setMobileDrawerOpen(false);
                }}
                onLogout={handleLogout}
              />
            </Box>
          </Drawer>
        )}

        <Box
          component="main"
          sx={{
            flexGrow: 1,
            width: "100%",
            minWidth: 0,
            pt: 3,
            pb: 3,
            px: { xs: 2, sm: 3, md: 4 },
          }}
        >
          <Outlet />
        </Box>
      </Paper>

      <CommandPalette
        open={commandPaletteOpen}
        onClose={() => setCommandPaletteOpen(false)}
      />
    </Box>
  );
}
